import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { MapPin, MessageCircle, Shield, Users, Eye, Zap, Heart, Globe, Lock, Sparkles } from 'lucide-react';

const Landing = () => {
  const navigate = useNavigate();
  const [showAgeModal, setShowAgeModal] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [memberCount, setMemberCount] = useState(15000);

  // Check if age was already verified (persisted in localStorage)
  const isAgeVerified = () => {
    const verified = localStorage.getItem('ageVerified');
    const verifiedAt = localStorage.getItem('ageVerifiedAt');
    
    if (!verified || !verifiedAt) return false;
    
    // Check if verification is still valid (90 days)
    const verifiedDate = new Date(verifiedAt);
    const now = new Date();
    const daysSinceVerification = (now - verifiedDate) / (1000 * 60 * 60 * 24);
    
    // Verification valid for 90 days
    return daysSinceVerification < 90;
  };

  // Animate member count on load
  useEffect(() => {
    const target = 15847;
    const duration = 2000;
    const step = (target - memberCount) / (duration / 50);
    const timer = setInterval(() => {
      setMemberCount(prev => {
        const next = prev + step;
        if (next >= target) {
          clearInterval(timer);
          return target;
        }
        return Math.floor(next);
      });
    }, 50);
    return () => clearInterval(timer);
  }, []);

  const handleEnterClick = () => {
    // Skip age modal if already verified
    if (isAgeVerified()) {
      navigate('/auth');
    } else {
      setShowAgeModal(true);
    }
  };

  const handleConfirmAge = () => {
    if (ageConfirmed) {
      // Store verification with timestamp
      localStorage.setItem('ageVerified', 'true');
      localStorage.setItem('ageVerifiedAt', new Date().toISOString());
      navigate('/auth');
    }
  };

  const features = [
    { icon: MapPin, title: 'Location-Based', desc: 'Find people near you' },
    { icon: MessageCircle, title: 'Private Chat', desc: 'Secure messaging' },
    { icon: Shield, title: 'Privacy First', desc: 'You control who sees you' },
    { icon: Eye, title: 'Incognito Mode', desc: 'Browse anonymously' },
    { icon: Zap, title: 'Real-Time', desc: 'See who\'s online now' },
    { icon: Heart, title: 'Connections', desc: 'Tap & match instantly' },
  ];

  const proFeatures = [
    { icon: Globe, title: 'Unlimited Browse', desc: 'See everyone nearby' },
    { icon: Lock, title: 'Private Albums', desc: 'Share exclusive photos' },
    { icon: Sparkles, title: 'Boost Profile', desc: 'Get seen by more people' },
  ];

  // Profile images for the grid showcase
  const profileImages = [
    'https://images.unsplash.com/photo-1635631191842-cc5043d56fc4?w=300&h=300&fit=crop',
    'https://images.unsplash.com/photo-1582439170934-d089aa10abda?w=300&h=300&fit=crop',
    'https://images.unsplash.com/photo-1635631191694-8fc637d53429?w=300&h=300&fit=crop',
    'https://images.unsplash.com/photo-1666819862436-0afec2604732?w=300&h=300&fit=crop',
    'https://images.unsplash.com/photo-1685328403755-de1d57e12e63?w=300&h=300&fit=crop',
    'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=300&fit=crop',
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen flex flex-col items-center justify-center px-6">
        {/* Background with gradient overlay */}
        <div className="absolute inset-0 z-0">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-40"
            style={{ 
              backgroundImage: 'url(https://images.unsplash.com/photo-1759178459426-1178fd1128e3?w=1920&q=80)',
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/60 via-[#050505]/80 to-[#050505]" />
          {/* Glow effect */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(255,46,99,0.15)_0%,transparent_60%)]" />
        </div>

        {/* Content */}
        <div className="relative z-10 w-full max-w-4xl text-center">
          {/* Logo */}
          <div className="mb-6 animate-fade-in">
            <h1 className="text-6xl md:text-8xl font-extrabold tracking-tighter" style={{ fontFamily: 'Syne, sans-serif' }}>
              <span className="text-[#FF2E63]">PULSE</span>
            </h1>
          </div>

          {/* Main Headline - Grindr style */}
          <h2 
            className="text-3xl md:text-5xl lg:text-6xl font-bold uppercase tracking-tight mb-4 leading-tight"
            style={{ fontFamily: 'Syne, sans-serif' }}
          >
            Connect.<br/>
            <span className="text-[#FF2E63]">Discover.</span><br/>
            Meet.
          </h2>

          {/* Subheadline */}
          <p className="text-lg md:text-xl text-[#A1A1AA] mb-8 max-w-2xl mx-auto">
            The social network built for the LGBTQ+ community.
            Find your people. Your way.
          </p>

          {/* Member Count - JACK'd style */}
          <div className="mb-10">
            <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm">
              <div className="w-3 h-3 bg-[#08D9D6] rounded-full animate-pulse" />
              <span className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {memberCount.toLocaleString()}+
              </span>
              <span className="text-[#A1A1AA]">members online</span>
            </div>
          </div>

          {/* CTA Button */}
          <Button 
            data-testid="enter-app-btn"
            onClick={handleEnterClick}
            className="px-12 py-7 text-lg font-bold uppercase tracking-widest bg-[#FF2E63] hover:bg-[#E01E50] text-white rounded-full shadow-[0_0_30px_rgba(255,46,99,0.4)] hover:shadow-[0_0_50px_rgba(255,46,99,0.6)] transition-all duration-300 transform hover:scale-105"
            style={{ fontFamily: 'Syne, sans-serif' }}
          >
            Get Started
          </Button>

          {/* App Store buttons placeholder */}
          <div className="mt-8 flex justify-center gap-4">
            <div className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-[#A1A1AA]">
              Available on Web
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-[#FF2E63] rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Features Grid Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h3 
            className="text-3xl md:text-4xl font-bold text-center mb-4 uppercase tracking-tight"
            style={{ fontFamily: 'Syne, sans-serif' }}
          >
            Meet Them <span className="text-[#FF2E63]">Here</span>
          </h3>
          <p className="text-[#A1A1AA] text-center mb-12 max-w-xl mx-auto">
            Everything you need to connect with people nearby
          </p>

          {/* Bento Grid Features */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="group p-6 rounded-2xl bg-white/5 border border-white/10 hover:border-[#FF2E63]/50 hover:bg-white/[0.07] transition-all duration-300"
              >
                <feature.icon className="w-8 h-8 text-[#FF2E63] mb-4 group-hover:scale-110 transition-transform" />
                <h4 className="text-lg font-bold mb-1" style={{ fontFamily: 'Syne, sans-serif' }}>{feature.title}</h4>
                <p className="text-sm text-[#A1A1AA]">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Profile Grid Showcase */}
      <section className="py-20 px-6 bg-gradient-to-b from-[#050505] via-[#0A0A0A] to-[#050505]">
        <div className="max-w-6xl mx-auto">
          <h3 
            className="text-3xl md:text-4xl font-bold text-center mb-4 uppercase tracking-tight"
            style={{ fontFamily: 'Syne, sans-serif' }}
          >
            Your <span className="text-[#FF2E63]">Community</span>
          </h3>
          <p className="text-[#A1A1AA] text-center mb-12 max-w-xl mx-auto">
            Join thousands already connecting on PULSE
          </p>

          {/* Profile Grid */}
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2 md:gap-3 max-w-4xl mx-auto mb-12">
            {profileImages.map((img, index) => (
              <div 
                key={index}
                className="aspect-square rounded-lg overflow-hidden relative group"
              >
                <img 
                  src={img} 
                  alt={`Community member ${index + 1}`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                {index < 3 && (
                  <div className="absolute top-2 right-2 w-3 h-3 bg-[#08D9D6] rounded-full border-2 border-[#050505]" />
                )}
              </div>
            ))}
          </div>

          {/* Stats */}
          <div className="flex justify-center gap-8 md:gap-16 text-center">
            <div>
              <div className="text-3xl md:text-4xl font-bold text-[#FF2E63]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>50K+</div>
              <div className="text-sm text-[#A1A1AA]">Connections Made</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-[#08D9D6]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>24/7</div>
              <div className="text-sm text-[#A1A1AA]">Active Community</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-[#FF2E63]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>100%</div>
              <div className="text-sm text-[#A1A1AA]">Privacy Focused</div>
            </div>
          </div>
        </div>
      </section>

      {/* Premium Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="relative p-8 md:p-12 rounded-3xl bg-gradient-to-br from-[#FF2E63]/20 via-[#0A0A0A] to-[#7000FF]/20 border border-white/10 overflow-hidden">
            {/* Glow */}
            <div className="absolute -top-20 -right-20 w-60 h-60 bg-[#FF2E63]/20 rounded-full blur-3xl" />
            <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-[#7000FF]/20 rounded-full blur-3xl" />
            
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#FFD700]/20 text-[#FFD700] text-sm font-bold mb-6">
                <Sparkles className="w-4 h-4" />
                PULSE ELITE
              </div>
              
              <h3 
                className="text-2xl md:text-4xl font-bold mb-4 uppercase tracking-tight"
                style={{ fontFamily: 'Syne, sans-serif' }}
              >
                Unlock the Full<br/><span className="text-[#FF2E63]">Experience</span>
              </h3>
              
              <p className="text-[#A1A1AA] mb-8 max-w-lg">
                Get unlimited access to premium features and connect with more people.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                {proFeatures.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3 p-4 rounded-xl bg-white/5">
                    <feature.icon className="w-6 h-6 text-[#FFD700]" />
                    <div>
                      <div className="font-bold text-sm">{feature.title}</div>
                      <div className="text-xs text-[#A1A1AA]">{feature.desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              <Button 
                data-testid="learn-premium-btn"
                onClick={handleEnterClick}
                variant="outline"
                className="px-8 py-6 font-bold uppercase tracking-wider border-[#FFD700] text-[#FFD700] hover:bg-[#FFD700]/10 rounded-full"
              >
                Learn More
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-6 text-center">
        <h3 
          className="text-4xl md:text-6xl font-bold mb-6 uppercase tracking-tight"
          style={{ fontFamily: 'Syne, sans-serif' }}
        >
          Ready to<br/><span className="text-[#FF2E63]">Connect?</span>
        </h3>
        <p className="text-[#A1A1AA] mb-8 max-w-lg mx-auto">
          Join the fastest-growing LGBTQ+ community app. Free to download.
        </p>
        <Button 
          data-testid="final-cta-btn"
          onClick={handleEnterClick}
          className="px-12 py-7 text-lg font-bold uppercase tracking-widest bg-[#FF2E63] hover:bg-[#E01E50] text-white rounded-full shadow-[0_0_30px_rgba(255,46,99,0.4)] hover:shadow-[0_0_50px_rgba(255,46,99,0.6)] transition-all duration-300 transform hover:scale-105"
          style={{ fontFamily: 'Syne, sans-serif' }}
        >
          Get Started Free
        </Button>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/10">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            <div className="text-center md:text-left">
              <h4 className="text-2xl font-bold text-[#FF2E63] mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>PULSE</h4>
              <p className="text-sm text-[#52525B]">Connect. Discover. Meet.</p>
            </div>
            
            <div className="flex flex-wrap justify-center gap-6 text-sm text-[#A1A1AA]">
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/faq" className="hover:text-white transition-colors">Help & Support</Link>
              <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
            </div>
          </div>
          
          <div className="mt-8 pt-8 border-t border-white/5 text-center text-xs text-[#52525B]">
            <p>&copy; {new Date().getFullYear()} PULSE. All rights reserved. Must be 18+ to use.</p>
          </div>
        </div>
      </footer>

      {/* Age Verification Modal */}
      <Dialog open={showAgeModal} onOpenChange={setShowAgeModal}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center" style={{ fontFamily: 'Syne, sans-serif' }}>
              Age Verification
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA] text-center">
              This app contains adult content and is intended for users 18 years of age or older.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-6">
            <div className="flex items-start space-x-3 p-4 rounded-lg bg-white/5 border border-white/10">
              <Checkbox 
                id="age-confirm"
                data-testid="age-confirm-checkbox"
                checked={ageConfirmed}
                onCheckedChange={setAgeConfirmed}
                className="mt-0.5 border-[#FF2E63] data-[state=checked]:bg-[#FF2E63]"
              />
              <label 
                htmlFor="age-confirm" 
                className="text-sm text-[#EDEDED] cursor-pointer leading-relaxed"
              >
                I confirm that I am at least 18 years of age and I agree to the{' '}
                <Link to="/terms" className="text-[#FF2E63] hover:underline" onClick={(e) => e.stopPropagation()}>
                  Terms of Service
                </Link>{' '}and{' '}
                <Link to="/privacy" className="text-[#FF2E63] hover:underline" onClick={(e) => e.stopPropagation()}>
                  Privacy Policy
                </Link>.
              </label>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-3">
            <Button 
              data-testid="cancel-age-btn"
              variant="ghost" 
              onClick={() => setShowAgeModal(false)}
              className="w-full sm:w-auto text-[#A1A1AA] hover:text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button 
              data-testid="confirm-age-btn"
              onClick={handleConfirmAge}
              disabled={!ageConfirmed}
              className="w-full sm:w-auto bg-[#FF2E63] hover:bg-[#E01E50] text-white disabled:opacity-50 disabled:cursor-not-allowed font-bold uppercase tracking-wide"
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Landing;
